/* eslint-disable @typescript-eslint/no-explicit-any */

// ***** Import from packages *****
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";

// ***** Import from files *****
import { BASE_API_URL } from "../constants/apiEndPoints";

// ***** Type definitions *****
type ApiMethod = "get" | "post" | "put" | "patch" | "delete";

interface ApiResponse<T = unknown> {
  status: number;
  data?: T;
  message?: string;
  ok?: boolean;
}

interface ApiError {
  status: number;
  message: string;
  data?: unknown;
}

interface AxiosErrorCustom extends AxiosError {
  response?: AxiosResponse<unknown> & {
    data?: {
      message?: string;
    };
  };
}

interface ApiOptions extends AxiosRequestConfig {
  headers?: Record<string, string>;
  responseType?: "json" | "stream";
}

// ***** Constants *****
const API_CONFIG = {
  baseURL: BASE_API_URL,
  timeout: 30000, // 30 seconds
  headers: {
    "Content-Type": "application/json",
  },
} as const;

// ***** Utility functions *****
const getUnStringified = (token: string | null): string => {
  return token?.replaceAll('"', "") ?? "";
};

const getToken = (): string | null => {
  return localStorage.getItem("token");
};

const createHeaders = (
  isNonAuthenticated: boolean,
  customHeaders: Record<string, string> = {},
): Record<string, string> => {
  const headers: any = {
    ...API_CONFIG.headers,
    ...customHeaders,
  };

  if (!isNonAuthenticated) {
    const token = getUnStringified(getToken());
    if (token) {
      headers["Authorization"] = `Token ${token}`;
    }
  }

  return headers;
};

/**
 * Makes an HTTP request to the specified endpoint
 * @param endpoint - API endpoint
 * @param data - Request payload
 * @param type - HTTP method
 * @param isNonAuthenticated - Whether to skip authentication
 * @param customHeader - Additional headers
 * @param isManualEndpoint - Whether to use endpoint as complete URL
 * @param customOptions - Additional axios options
 * @param isStream - Whether response is a stream
 */
export const api = async <T = unknown>(
  endpoint: string,
  data: unknown = {},
  type: ApiMethod,
  isNonAuthenticated = false,
  customHeader: Record<string, string> = {},
  isManualEndpoint = false,
  customOptions: ApiOptions = {},
  isStream = false,
): Promise<ApiResponse<T | Blob>> => {
  try {
    const headers = createHeaders(isNonAuthenticated, customHeader);
    const url = isManualEndpoint
      ? endpoint
      : `${API_CONFIG.baseURL || ""}${endpoint}`;

    const config: AxiosRequestConfig = {
      ...customOptions,
      headers,
      responseType: isStream ? "blob" : "json",
    };

    let response: AxiosResponse<T>;

    switch (type) {
      case "get":
        response = await axios.get<T>(url, config);
        break;
      case "delete":
        response = await axios.delete<T>(url, { ...config, data });
        break;
      case "post":
      case "put":
      case "patch":
        response = await axios[type]<T>(url, data, config);
        break;
      default:
        throw new Error(`Invalid request type: ${type}`);
    }

    return {
      status: response.status,
      data: isStream ? (response.data as Blob) : response.data,
      ok: true,
    };
  } catch (error) {
    const axiosError = error as AxiosErrorCustom;
    const apiError: ApiError = {
      status: axiosError.response?.status ?? 500,
      message:
        axiosError.response?.data?.message ??
        axiosError.message ??
        "Unknown error occurred",
      data: axiosError.response?.data,
    };

    return {
      status: apiError.status,
      message: apiError.message,
      data: apiError.data as T,
      ok: false,
    };
  }
};

// Optional: Add request/response interceptors
axios.interceptors.request.use(
  (config) => {
    // Add any request transformations
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (response) => {
    // Add any response transformations
    return response;
  },
  (error) => {
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem("token");
      // Optionally redirect to login
    }
    return Promise.reject(error);
  },
);
